import { useEffect, useMemo, useState, useRef } from "react";
import CaseCard from "./CaseCard";
import Pagination from "./Util/Pagination";
import ToggleView from "./Visual/ToggleView";
import { useNavigate, useLocation } from "react-router-dom";
import ToggleSyzitisi from "./Visual/ToggleSyzitisi";
import { parseAsInteger, useQueryState } from "nuqs";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import {
  applyCaseFilters,
  getSelectedDiadikasia,
  sortCases,
} from "../features/cases/caseFilters";
import { startBulkCheckAll } from "../features/cases/caseActions";

const Cases = ({
  cases,
  prothesmies,
  handleDeleteCase,
  handleAddSelectedDiadikasia,
  credits,
  setCredits,
}) => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const decisions = urlParams.get("decisions");
  const syzitisi = urlParams.get("syzitisi");
  const diadikasia = urlParams.get("diadikasia");
  const court = urlParams.get("court");
  const epan = urlParams.get("epan");
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem("casesSortBy") || "created_desc";
  });
  const [bulkChecking, setBulkChecking] = useState(false);
  const totalCasesRef = useRef(null);

  const handleSortChange = (value) => {
    setSortBy(value);
    localStorage.setItem("casesSortBy", value);
  };

  const casesSorted = useMemo(() => sortCases(cases, sortBy), [cases, sortBy]);
  const [page, setPage] = useQueryState(
    "page",
    parseAsInteger.withDefault(0).withOptions({ history: "replace" })
  );
  function dividePages(arr) {
    const res = [];
    for (let index = 0; index < arr.length; index++) {
      const el = [];
      if (index % 5 === 0) {
        res.push(el);
      }

      let i = Math.floor(index / 5);
      res[i].push(arr[index]);
    }
    return res;
  }

  const handleSelect = (e) => {
    const urlParams = new URLSearchParams(location.search);

    const mapObj = { 2: "all", 0: "yes", 1: "no" };
    const combineParams = new URLSearchParams({
      decisions: mapObj[e],
      syzitisi: urlParams.get("syzitisi"),
      court: urlParams.get("court"),
      diadikasia: urlParams.get("diadikasia"),
      epan: urlParams.get("epan"),
    });
    navigate(location.pathname + "?" + combineParams.toString(), {
      replace: true,
    });
  };
  const handleSyzitisi = (e) => {
    const urlParams = new URLSearchParams(location.search);

    const mapObj = { 2: "all", 0: "yes", 1: "no" };
    const combineParams = new URLSearchParams({
      decisions: urlParams.get("decisions"),
      syzitisi: mapObj[e],
      court: urlParams.get("court"),
      diadikasia: urlParams.get("diadikasia"),
      epan: urlParams.get("epan"),
    });
    navigate(location.pathname + "?" + combineParams.toString(), {
      replace: true,
    });
  };
  const handleEpan = (value) => {
    const urlParams = new URLSearchParams(location.search);

    const combineParams = new URLSearchParams({
      decisions: urlParams.get("decisions"),
      syzitisi: urlParams.get("syzitisi"),
      court: urlParams.get("court"),
      diadikasia: urlParams.get("diadikasia"),
      epan: value || null,
    });
    navigate(location.pathname + "?" + combineParams.toString(), {
      replace: true,
    });
  };
  const hasEpanaprosdiorismos = cases?.some((c) => c.epanaprosdiorismos);
  const filteredCases = useMemo(
    () =>
      applyCaseFilters(casesSorted, {
        decisions,
        syzitisi,
        diadikasia,
        court,
        epan,
      }),
    [casesSorted, decisions, syzitisi, diadikasia, court, epan]
  );

  const paginatedCases = useMemo(
    () => dividePages(filteredCases),
    [filteredCases]
  );
  const pagesCount = paginatedCases.length;

  const handleCheckAll = async () => {
    if (!cases?.length || bulkChecking) return;

    setBulkChecking(true);
    const { message } = await startBulkCheckAll(axiosPrivate);
    window.alert(message);
    setBulkChecking(false);
  };

  const handlePage = (e) => {
    const action = e.target.getAttribute("data-key");
    const lastIndex = Math.max(pagesCount - 1, 0);

    if (action === "next") {
      setPage((old) => Math.min(old + 1, lastIndex));
      return;
    }

    if (action === "end") {
      setPage(lastIndex);
      return;
    }

    if (action === "previous") {
      setPage((old) => Math.max(old - 1, 0));
      return;
    }

    if (action === "start") {
      setPage(0);
    }
  };

  useEffect(() => {
    if (pagesCount === 0) {
      return;
    }

    const lastPageIndex = pagesCount - 1;
    if (page > lastPageIndex) {
      setPage(lastPageIndex);
    }
  }, [page, pagesCount, setPage]);

  useEffect(() => {
    if (totalCasesRef.current) {
      totalCasesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [page]);

  // string to lowercase
  // string to uppercase
  // string to lowercase

  return (
    <article>
      {cases?.length ? (
        <>
          <div className="toggle-view">
            <ToggleView handleSelect={handleSelect} />
          </div>
          <div className="toggle-view">
            <ToggleSyzitisi handleSelect={handleSyzitisi} />
          </div>
          <div className="toggle-view mt-2">
            <label htmlFor="sort-select" style={{ marginRight: "8px" }}>
              Ταξινόμηση:
            </label>
            <select
              id="sort-select"
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              style={{
                padding: "4px 8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            >
              <option value="created_desc">Ημ/νία Δημιουργίας (Νεότερη)</option>
              <option value="created_asc">
                Ημ/νία Δημιουργίας (Παλαιότερη)
              </option>
              <option value="court_asc">Δικαστήριο (Α-Ω)</option>
              <option value="court_desc">Δικαστήριο (Ω-Α)</option>
              <option value="last_desc">Τελ. Έλεγχος (Νεότερη)</option>
              <option value="last_asc">Τελ. Έλεγχος (Παλαιότερη)</option>
              <option value="fav_desc">Αγαπημένα (Πρώτα)</option>
              <option value="fav_asc">Αγαπημένα (Τελευταία)</option>
            </select>
          </div>
          {hasEpanaprosdiorismos && (
            <div className="toggle-view mt-2">
              <label htmlFor="epan-select" style={{ marginRight: "8px" }}>
                Επαναπροσδιορισμός ανακοπών:
              </label>
              <select
                id="epan-select"
                value={epan && epan !== "null" ? epan : ""}
                onChange={(e) => handleEpan(e.target.value)}
                style={{
                  padding: "4px 8px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
              >
                <option value="">Όλες οι υποθέσεις</option>
                <option value="pending">Χρειάζονται αίτηση</option>
                <option value="filed">Κατατέθηκε αίτηση</option>
                <option value="application">Αιτήσεις επαναπροσδιορισμού</option>
              </select>
            </div>
          )}
          <h4 className="total-cases" ref={totalCasesRef}>
            Σύνολο Υποθέσεων: {filteredCases.length}
          </h4>
          <button
            type="button"
            className={`addcase_modal check_now ${bulkChecking ? "button--loading" : ""}`}
            onClick={handleCheckAll}
            disabled={bulkChecking || !cases?.length}
            style={{ marginBottom: "10px" }}
          >
            {bulkChecking ? "Γίνεται εκκίνηση..." : "Ελέγξτε όλες τις υποθέσεις"}
          </button>

          <Pagination page={page} length={pagesCount} handlePage={handlePage} />

          {filteredCases &&
            paginatedCases[page]?.map((singleCase, i) => {
              const no = page * 5 + i;
              const {
                id,
                gak,
                year,
                court,
                diadikasia,
                antikeimeno,
                eak,
                arithmos_pinakiou,
                arithmos_apofasis,
                apotelesma,
                note,
                imerominia_katathesis,
                check_appeal,
                appeal,
                created_at,
                last_checked,
                userInput,
                appeal_links,
              } = singleCase;
              const selectedDiadikasia = getSelectedDiadikasia(singleCase);
              const prothesmiesSingle = prothesmies?.filter(
                (r) => r.gak_id === id
              );
              return (
                <CaseCard
                  key={id}
                  singleCase={{
                    no,
                    i,
                    id,
                    gak,
                    year,
                    court,
                    diadikasia,
                    antikeimeno,
                    eak,
                    arithmos_pinakiou,
                    arithmos_apofasis,
                    apotelesma,
                    imerominia_katathesis,
                    note,
                    check_appeal,
                    appeal,
                    created_at,
                    last_checked,
                    userInput,
                    selectedDiadikasia,
                    appeal_links,
                    fav: singleCase.fav,
                    pretrial_order: singleCase.pretrial_order,
                    epanaprosdiorismos: singleCase.epanaprosdiorismos,
                  }}
                  prothesmiesSingle={prothesmiesSingle}
                  handleDeleteCase={handleDeleteCase}
                  handleAddSelectedDiadikasia={handleAddSelectedDiadikasia}
                  credits={credits}
                  setCredits={setCredits}
                />
              );
            })}
          <Pagination page={page} length={pagesCount} handlePage={handlePage} />
        </>
      ) : (
        <p>Δεν έχετε καταχωρήσει υποθέσεις</p>
      )}
    </article>
  );
};

export default Cases;
